/**
 * @file SourceLinkedNarrative.tsx
 *
 * @description The rendered state of a citation-bearing narrative: the prose
 * with its source anchors, framed like a form field. While a narrative still
 * carries provenance, hosts render THIS instead of the textarea; `onEdit`
 * swaps into the editor (omit it for readonly hosts, which get the same block
 * without the button). SourceLinkedEditBar is the companion strip shown above
 * the textarea while the markers are still intact — the way back, plus the
 * warning that changing the text unlinks its sources.
 */
import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { CitedNarrative } from "./CitedNarrative";
import type { CanCite } from "../../../features/narrative-citations/renderCitedSegments";
import type { CitationGroup } from "../../../features/narrative-citations/types";

/**
 * State for the view/edit swap, shared by both narrative hosts.
 *
 * - `enterEdit` — the "Edit text" click; the textarea mounts with autoFocus.
 * - `latchEdit` — wired to the textarea's focus: a narrative the officer is
 *   actively inside must never swap out from under them when an external
 *   update (chat-driven rewrite, websocket refresh) lands citation-bearing
 *   text — the edit bar appears instead and offers the view.
 * - `exitEdit` — "View linked sources"; the swap unmounts the button that
 *   held focus, so the returning view's Edit button takes it (autoFocusEdit).
 * - `viewForced` (readonly / editing-off) resets both flags: without this, a
 *   save cycle that briefly forces the view would later remount the textarea
 *   with autoFocus and steal focus the officer never asked for.
 */
export function useSourceLinkedSwap(viewForced: boolean): {
  proseEditing: boolean;
  autoFocusEdit: boolean;
  enterEdit: () => void;
  latchEdit: () => void;
  exitEdit: () => void;
} {
  const [proseEditing, setProseEditing] = useState(false);
  const [autoFocusEdit, setAutoFocusEdit] = useState(false);

  useEffect(() => {
    if (viewForced) {
      setProseEditing(false);
      setAutoFocusEdit(false);
    }
  }, [viewForced]);

  return {
    proseEditing,
    autoFocusEdit,
    enterEdit: () => {
      setAutoFocusEdit(false);
      setProseEditing(true);
    },
    latchEdit: () => setProseEditing(true),
    exitEdit: () => {
      setProseEditing(false);
      setAutoFocusEdit(true);
    },
  };
}

export interface SourceLinkedNarrativeProps {
  id: string;
  label: string;
  required?: boolean;
  description?: string;
  /** Matches the textarea it replaces so the swap doesn't jump the layout. */
  minRows: number;
  /** Raw narrative text WITH citation tokens. */
  text: string;
  onCite?: (group: CitationGroup) => void;
  canCite?: CanCite;
  /** Swap the host into the textarea. Omit for readonly hosts. */
  onEdit?: () => void;
  editDisabled?: boolean;
  /** Focus the Edit button on mount — set when the view returns via "View
   * linked sources", whose click unmounted the element holding focus. */
  autoFocusEdit?: boolean;
}

export const SourceLinkedNarrative: React.FC<SourceLinkedNarrativeProps> = ({
  id,
  label,
  required,
  description,
  minRows,
  text,
  onCite,
  canCite,
  onEdit,
  editDisabled,
  autoFocusEdit,
}) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 0.75,
      }}
    >
      <Typography
        id={`${id}-citation-label`}
        component="div"
        variant="body2"
        color="text.secondary"
      >
        {label}
        {required ? " *" : ""}
      </Typography>
      {onEdit && (
        <Button
          size="small"
          startIcon={<EditIcon fontSize="small" />}
          onClick={onEdit}
          disabled={editDisabled}
          autoFocus={autoFocusEdit}
          // The visible text must lead the accessible name contiguously
          // (WCAG 2.5.3 — voice control matches spoken commands against it).
          aria-label={`Edit text — ${label}`}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          Edit text
        </Button>
      )}
    </Box>
    <Box
      id={id}
      role="group"
      aria-labelledby={`${id}-citation-label`}
      // The RJSF error list focuses fields by id (getElementById + focus());
      // a plain div ignores focus() without an explicit tabindex.
      tabIndex={-1}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.75,
        py: 1.5,
        minHeight: `${minRows * 1.5}em`,
        bgcolor: "action.hover",
      }}
    >
      <CitedNarrative text={text} onCite={onCite} canCite={canCite} />
    </Box>
    {description && (
      <Typography
        component="div"
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5 }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

export interface SourceLinkedEditBarProps {
  onDone: () => void;
  disabled?: boolean;
}

/** Strip above the textarea while its narrative still carries citations. */
export const SourceLinkedEditBar: React.FC<SourceLinkedEditBarProps> = ({
  onDone,
  disabled,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    }}
  >
    <Typography variant="caption" color="text.secondary">
      Editing a cited sentence unlinks its source — untouched sources stay
      linked.
    </Typography>
    <Button
      size="small"
      onClick={onDone}
      disabled={disabled}
      sx={{ textTransform: "none", flexShrink: 0 }}
    >
      View linked sources
    </Button>
  </Box>
);
